import React from 'react';
import s from './ai.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SuggestedActionsProps {
  page?: string;
  onAction: (message: string) => void;
  className?: string;
}

interface ActionChip {
  label: string;
  message: string;
}

// ─── Page → Actions Map ───────────────────────────────────────────────────────

function getActionsForPage(page?: string): ActionChip[] {
  switch (page) {
    case 'deals':
    case '/deals':
      return [
        { label: 'Review pipeline', message: 'Review my current deal pipeline and flag any that need attention.' },
        { label: 'Draft escalation email', message: 'Write an escalation email for my most overdue payment.' },
        { label: 'Add a new deal', message: 'Help me add a new brand deal.' },
      ];

    case 'payments':
    case '/payments':
      return [
        { label: 'Draft escalation email', message: 'Write an escalation email for my most overdue payment.' },
        { label: 'Check payment health', message: 'Analyze my payment history and flag any patterns or red flags.' },
        { label: 'Invoice summary', message: 'Give me a summary of my outstanding invoices.' },
      ];

    case 'brands':
    case '/brands':
      return [
        { label: 'Find brands to pitch', message: 'Based on my niche and stats, what types of brands should I be targeting?' },
        { label: 'Re-engage a brand', message: 'Help me write a re-engagement message for a brand I worked with before.' },
        { label: 'Check for red flags', message: 'Are there any brands in my list that have payment red flags?' },
      ];

    case 'outreach':
    case '/outreach':
      return [
        { label: 'Write a pitch', message: 'Help me write a cold outreach pitch for a new brand partnership.' },
        { label: 'Follow up on sent pitches', message: 'Draft a follow-up message for pitches I sent last week.' },
        { label: 'Find brands to pitch', message: 'What brands should I be targeting based on my audience?' },
      ];

    case 'finances':
    case '/finances':
      return [
        { label: 'Revenue summary', message: 'Give me a summary of my revenue for the last 90 days.' },
        { label: 'Find income gaps', message: 'Analyze my income and identify gaps or slow months I should plan for.' },
        { label: 'Tax prep tips', message: 'What should I be tracking for taxes as a creator?' },
      ];

    case 'analytics':
    case '/analytics':
      return [
        { label: 'Analyze engagement', message: 'Analyze my engagement rates and tell me if they are above or below average.' },
        { label: 'Rate recommendations', message: 'Based on my current stats, what rates should I be charging?' },
        { label: 'Growth opportunities', message: 'Where should I focus to grow my audience fastest?' },
      ];

    case 'goals':
    case '/goals':
      return [
        { label: 'Check goal progress', message: 'How am I tracking against my annual revenue goal?' },
        { label: 'Strategy advice', message: 'What should I focus on to hit my revenue goal this month?' },
        { label: 'Set new goal', message: 'Help me think through a realistic revenue goal for this quarter.' },
      ];

    case 'legal':
    case '/legal':
      return [
        { label: 'Review contract terms', message: 'What are the most important clauses to watch for in a brand contract?' },
        { label: 'Check exclusivities', message: 'Do I have any active exclusivity clauses that might conflict with new deals?' },
        { label: 'Draft NDA request', message: 'Help me draft an NDA request for a brand in early conversations.' },
      ];

    case 'collabs':
    case '/collabs':
      return [
        { label: 'Find collab partners', message: 'What type of creators should I be collaborating with based on my niche?' },
        { label: 'Draft collab proposal', message: 'Help me write a collaboration proposal for another creator.' },
        { label: 'Split payment advice', message: 'How should I structure payment splits on a brand collab deal?' },
      ];

    default:
      return [
        { label: 'Review my pipeline', message: 'Review my current deal pipeline and flag anything urgent.' },
        { label: 'Draft escalation email', message: 'Write an escalation email for my most overdue payment.' },
        { label: 'Rate advice', message: 'Based on my stats and recent deals, am I charging what I should be?' },
      ];
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export const SuggestedActions: React.FC<SuggestedActionsProps> = ({
  page,
  onAction,
}) => {
  const actions = getActionsForPage(page);

  if (actions.length === 0) return null;

  return (
    <div className={s.suggestedWrap}>
      {actions.map((action) => (
        <button
          key={action.label}
          onClick={() => onAction(action.message)}
          className={s.suggestedBtn}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
};
